import { durationMinutes, formatDuration } from '@fonus/shared';
import type { ReactNode } from 'react';
import type { T } from '../i18n/useT.ts';
import { Chip, type ChipTone } from './controls.tsx';
import { Cover } from './Cover.tsx';
import { CheckIcon, EqualizerIcon, InfoIcon, LockIcon, PauseIcon, PlayIcon } from './icons.tsx';
import s from './story.module.css';

export type RowState = 'default' | 'playing' | 'listened' | 'locked' | 'draft';

export function StoryRow({
  title,
  hue,
  meta,
  state,
  isPlaying,
  onClick,
  t,
}: {
  title: string;
  hue: number;
  meta: ReactNode;
  state: RowState;
  isPlaying: boolean;
  onClick: () => void;
  t: T;
}) {
  return (
    <button type="button" className={s.row} onClick={onClick}>
      {state === 'draft' ? (
        <span className={s.draftCover} aria-hidden="true" />
      ) : (
        <Cover hue={hue} size={56} radius={14} />
      )}
      <span className={s.rowBody}>
        <span className={s.rowText}>
          <span
            className={[
              s.rowTitle,
              'ellipsis',
              state === 'playing' && s.rowTitlePlaying,
              state === 'draft' && s.rowTitleDraft,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {title}
          </span>
          <span className={`${s.rowMeta} tabular`}>{meta}</span>
        </span>
        {state === 'default' && (
          <span className={`${s.badge} ${s.badgePlay}`} aria-label={t('story.play')} role="img">
            <PlayIcon size={16} />
          </span>
        )}
        {state === 'playing' && <EqualizerIcon label={t('story.playing')} animate={isPlaying} />}
        {state === 'listened' && (
          <span
            className={`${s.badge} ${s.badgeMuted}`}
            aria-label={t('story.listened')}
            role="img"
          >
            <CheckIcon size={18} />
          </span>
        )}
        {state === 'locked' && (
          <span className={`${s.badge} ${s.badgeMuted}`} aria-label={t('story.locked')} role="img">
            <LockIcon size={17} />
          </span>
        )}
        {state === 'draft' && <Chip tone="muted">{t('story.draft')}</Chip>}
      </span>
    </button>
  );
}

/** Quota chip (Components · Quota chip). */
export function quotaChip(
  access: { freeLeft: number; freeLimit: number; premiumUntil: string | null },
  t: T,
): { tone: ChipTone; text: string } {
  if (access.premiumUntil) return { tone: 'premium', text: t('quota.premium') };
  if (access.freeLeft <= 0) return { tone: 'muted', text: t('quota.none') };
  if (access.freeLeft >= access.freeLimit)
    return { tone: 'free', text: t('quota.free', { count: access.freeLeft }) };
  return { tone: 'free', text: t('quota.freeLeft', { count: access.freeLeft }) };
}

export function MiniPlayer({
  title,
  hue,
  position,
  duration,
  playing,
  onOpen,
  onToggle,
  t,
}: {
  title: string;
  hue: number;
  position: number;
  duration: number;
  playing: boolean;
  onOpen: () => void;
  onToggle: () => void;
  t: T;
}) {
  const pct = duration > 0 ? Math.min(100, (position / duration) * 100) : 0;
  return (
    <div className={s.mini} role="region" aria-label={t('player.label')}>
      <div className={s.miniTrack} aria-hidden="true" />
      <div className={s.miniFill} style={{ width: `${pct}%` }} aria-hidden="true" />
      <button type="button" className={s.miniOpen} onClick={onOpen}>
        <Cover hue={hue} size={44} radius={11} />
        <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span className="t-subhead ellipsis" style={{ fontWeight: 700 }}>
            {title}
          </span>
          <span className="t-footnote secondary tabular">
            {formatDuration(position)} / {formatDuration(duration)}
          </span>
        </span>
      </button>
      <button
        type="button"
        className={s.miniButton}
        aria-label={playing ? t('player.pause') : t('player.play')}
        onClick={onToggle}
      >
        {playing ? <PauseIcon size={26} /> : <PlayIcon size={26} />}
      </button>
    </div>
  );
}

export function ToastView({ text, tone }: { text: string; tone: 'success' | 'info' }) {
  return (
    <div className={s.toast} role="status" aria-live="polite">
      {tone === 'success' ? (
        <CheckIcon size={18} width={2.6} style={{ color: 'var(--accent-text)' }} />
      ) : (
        <InfoIcon size={18} />
      )}
      {text}
    </div>
  );
}

export function minutesLabel(sec: number | null, t: T): string {
  return t('story.minutes', { count: durationMinutes(sec) });
}

export const storyStyles = s;
